<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue'
import type { SidebarMenuItem } from '@/types/sidebar.types'
import { SIDEBAR_INJECTION_KEY } from '@/types/sidebar.types'
import JIcon from '@/components/atoms/JIcon.vue'
import JSidebarItem from './JSidebarItem.vue'
import { cn } from '@/lib/utils'

/**
 * JSidebarGroup - 그룹 헤더 (menuType='F' 전용)
 * 폴더 타입 메뉴를 렌더링. 펼침/접힘 토글 + 하위 메뉴 재귀 렌더링.
 * 링크(L)는 JSidebarItem에서 처리.
 */

const props = defineProps<{
  item: SidebarMenuItem
}>()

const emit = defineEmits<{
  'menu-click': [item: SidebarMenuItem, event: MouseEvent]
}>()

const state = inject(SIDEBAR_INJECTION_KEY)!

const isExpanded = ref(false)

/** 직계 자식 중 L 타입 개수 (재귀하지 않음) */
const childCount = computed(() => {
  if (!props.item.children) return 0
  return props.item.children.filter(c => c.menuType === 'L').length
})

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
}

/** 자식의 menu-click을 상위로 전파 */
const handleChildClick = (item: SidebarMenuItem, event: MouseEvent) => {
  emit('menu-click', item, event)
}

/** 현재 경로가 이 그룹의 자식에 포함되면 자동 펼침 */
const hasActiveChild = computed(() => {
  if (!state.activePath || !props.item.children) return false
  const checkActive = (items: SidebarMenuItem[]): boolean => {
    for (const child of items) {
      if (child.menuType === 'L' && child.path === state.activePath) return true
      if (child.children && checkActive(child.children)) return true
    }
    return false
  }
  return checkActive(props.item.children)
})

watch(hasActiveChild, (active) => {
  if (active) isExpanded.value = true
}, { immediate: true })
</script>

<template>
  <!-- Collapsed: 구분선만 -->
  <template v-if="state.collapsed">
    <div class="h-px bg-border mx-2 my-1" />
    <!-- collapsed 상태에서 자식 아이템 직접 렌더링 -->
    <template v-for="child in item.children" :key="child.id">
      <JSidebarItem
        v-if="child.menuType === 'L'"
        :item="child"
        @menu-click="handleChildClick"
      />
      <!-- 중첩 폴더는 collapsed에서 무시 (1단계만) -->
    </template>
  </template>

  <!-- Expanded: 그룹 헤더 + 하위 메뉴 -->
  <div v-else>
    <!-- 그룹 헤더 -->
    <button
      class="flex items-center gap-1.5 w-full px-2 py-1 mt-0.5 text-xs font-semibold text-muted-foreground cursor-pointer select-none transition-colors hover:text-foreground"
      @click="toggleExpand"
    >
      <JIcon
        name="chevronRight"
        size="sm"
        :class="cn(
          'flex-shrink-0 transition-transform duration-200',
          isExpanded && 'rotate-90'
        )"
      />
      <span class="flex-1 truncate text-left">{{ item.label }}</span>
      <span
        v-if="childCount > 0"
        class="text-[10px] text-muted-foreground/60 flex-shrink-0"
      >
        {{ childCount }}
      </span>
    </button>

    <!-- 하위 메뉴 (grid-template-rows 애니메이션) -->
    <div
      :class="cn(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )"
    >
      <div class="overflow-hidden ml-3 pl-2 border-l border-border/50">
        <template v-for="child in item.children" :key="child.id">
          <JSidebarItem
            v-if="child.menuType === 'L'"
            :item="child"
            @menu-click="handleChildClick"
          />
          <!-- 중첩 폴더: 재귀 -->
          <JSidebarGroup
            v-else-if="child.menuType === 'F'"
            :item="child"
            @menu-click="handleChildClick"
          />
        </template>
      </div>
    </div>
  </div>
</template>
